import React, { useState } from "react";
import { FloppyDisk, FolderOpen, Trash, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

// Named player-list manager (localStorage) shared by Pool Analyzer + Fixture
// Matrix. Lists saved in one tab can be loaded in the other.

const LS_KEY = "fpl-saved-lists-v1";

const readLists = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(LS_KEY));
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
};

const writeLists = (lists) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(lists)); } catch { /* ignore */ }
};

export function SavedLists({ players, onLoad, source }) {
    const [lists, setLists] = useState(readLists);
    const [showSave, setShowSave] = useState(false);
    const [name, setName] = useState("");
    const [selectedId, setSelectedId] = useState("");

    const doSave = () => {
        const nm = name.trim();
        if (!nm) { toast.error("Zadej název seznamu"); return; }
        if (!players.length) { toast.error("Seznam je prázdný — není co uložit"); return; }
        const entry = {
            id: `${Date.now()}`,
            name: nm,
            source,
            count: players.length,
            players: players.map((p) => ({ ...p })),
            saved_at: new Date().toISOString(),
        };
        const next = [...lists.filter((l) => l.name !== nm), entry];
        setLists(next);
        writeLists(next);
        setName("");
        setShowSave(false);
        toast.success(`Seznam „${nm}" uložen (${players.length} hráčů).`);
    };

    const doLoad = () => {
        const l = lists.find((x) => x.id === selectedId);
        if (!l) { toast.error("Vyber uložený seznam"); return; }
        onLoad(l.players);
        toast.success(`Načten seznam „${l.name}" — ${l.players.length} hráčů.`);
    };

    const doDelete = () => {
        const l = lists.find((x) => x.id === selectedId);
        if (!l) { toast.error("Vyber seznam ke smazání"); return; }
        const next = lists.filter((x) => x.id !== selectedId);
        setLists(next);
        writeLists(next);
        setSelectedId("");
        toast.success(`Seznam „${l.name}" smazán.`);
    };

    return (
        <div className="flex items-center gap-2 flex-wrap font-mono text-[10px]" data-testid={`saved-lists-${source}`}>
            <span className="uppercase tracking-widest text-white/45">Seznamy:</span>
            {!showSave ? (
                <button
                    type="button"
                    onClick={() => setShowSave(true)}
                    disabled={!players.length}
                    data-testid={`saved-lists-${source}-save-toggle`}
                    className="uppercase tracking-widest px-2 py-1 border border-fa-yellow/40 text-fa-yellow hover:bg-fa-yellow hover:text-black disabled:opacity-30 flex items-center gap-1"
                    title={players.length ? `Uložit aktuálních ${players.length} hráčů jako pojmenovaný seznam` : "Seznam je prázdný"}
                >
                    <FloppyDisk size={10} weight="bold" />
                    Uložit seznam
                </button>
            ) : (
                <span className="flex items-center gap-1">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") doSave(); if (e.key === "Escape") setShowSave(false); }}
                        placeholder="Název seznamu…"
                        autoFocus
                        data-testid={`saved-lists-${source}-name-input`}
                        className="bg-black/40 border border-fa-yellow/40 px-2 py-1 w-40 focus:outline-none focus:border-fa-yellow"
                    />
                    <button type="button" onClick={doSave} data-testid={`saved-lists-${source}-save-confirm`}
                            className="px-1.5 py-1 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black">
                        <Check size={11} weight="bold" />
                    </button>
                    <button type="button" onClick={() => { setShowSave(false); setName(""); }}
                            className="px-1.5 py-1 border border-white/15 text-white/50 hover:text-white">
                        <X size={11} weight="bold" />
                    </button>
                </span>
            )}
            {lists.length > 0 && (
                <>
                    <select
                        value={selectedId}
                        onChange={(e) => setSelectedId(e.target.value)}
                        data-testid={`saved-lists-${source}-select`}
                        className="bg-black/40 border border-white/15 px-2 py-1 focus:outline-none focus:border-fa-sky max-w-[220px]"
                    >
                        <option value="">Uložené seznamy ({lists.length})…</option>
                        {lists.map((l) => (
                            <option key={l.id} value={l.id}>
                                {l.name} · {l.count} hráčů · {l.source === "pool" ? "Pool" : "Matice"}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={doLoad}
                        disabled={!selectedId}
                        data-testid={`saved-lists-${source}-load`}
                        className="uppercase tracking-widest px-2 py-1 border border-fa-sky/40 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-30 flex items-center gap-1"
                    >
                        <FolderOpen size={10} weight="bold" />
                        Načíst
                    </button>
                    <button
                        type="button"
                        onClick={doDelete}
                        disabled={!selectedId}
                        data-testid={`saved-lists-${source}-delete`}
                        className="px-1.5 py-1 border border-red-300/40 text-red-300 hover:bg-red-300 hover:text-black disabled:opacity-30"
                        title="Smazat vybraný seznam"
                    >
                        <Trash size={10} weight="bold" />
                    </button>
                </>
            )}
        </div>
    );
}
